// Three public routes carry a bearer token as the last path segment. The app
// already goes out of its way to keep those tokens from leaking (see the
// `Referrer-Policy: no-referrer` header on /wedding/register in next.config.ts)
// — Sentry must not undo that by recording the raw URL on every event,
// breadcrumb and transaction name.
const TOKEN_PATH = /\/(share|wedding\/details|wedding\/register)\/[^/?#]+/g;

export function redactTokenPath(value: string): string {
  return value.replace(TOKEN_PATH, (_match, route: string) => `/${route}/[token]`);
}

// Structural subset of Sentry's Event that we touch. Typed loosely on purpose
// so the same function serves beforeSend on the client, server and edge inits
// without importing three runtime-specific event types.
type ScrubbableEvent = {
  request?: { url?: string };
  transaction?: string;
  breadcrumbs?: Array<{ message?: string; data?: { [key: string]: unknown } }>;
};

// Mutates in place — Sentry hands us the event it is about to send, and
// returning it sends the edit.
export function scrubEvent<T extends ScrubbableEvent>(event: T): T {
  if (event.request?.url) event.request.url = redactTokenPath(event.request.url);
  if (event.transaction) event.transaction = redactTokenPath(event.transaction);
  for (const crumb of event.breadcrumbs ?? []) {
    if (typeof crumb.message === "string") crumb.message = redactTokenPath(crumb.message);
    if (typeof crumb.data?.url === "string") crumb.data.url = redactTokenPath(crumb.data.url);
  }
  return event;
}
